"use client"

import { useCallback, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { asArray } from "@/lib/as-array"
import {
  useDeleteTasksTaskId,
  useGetTasks,
  usePostTasks,
  usePutTasksTaskId,
} from "openapi/api/endpoints"
import type { Task, TaskPriority } from "openapi/api/model"
import { invalidateTaskQueries } from "@/lib/query-client"

export type TaskScope = "job" | "all"

/**
 * Statuses render lowercase exactly as stored, per canvas 14c — the one
 * exception is `inProgress`, which is stored camelCase and reads as two words.
 */
const STATUS_TEXT: Record<string, string> = {
  todo: "to do",
  inProgress: "in progress",
  blocked: "blocked",
  done: "done",
}

/**
 * A task as `GET /tasks` actually returns one.
 *
 * The read side names the title `task_title`; the write side takes `title`, and
 * the generated model only knows the second because that is all the spec
 * describes. Reading `title` therefore gave every row "Untitled task" —
 * including tasks this record had just written with a title in them, which is
 * how the bug was found. The rest of the app has been casting for `task_title`
 * since before the rebuild; this states it once instead.
 *
 * Backend ask: have the two sides agree on one name, or put `task_title` in the
 * spec so orval generates it.
 */
type StoredTask = Task & { task_title?: string }

export type TaskRow = {
  /** Stable for React keys; may be synthesized when the row carries no id. */
  id: string
  /**
   * The id the API knows, when there is one. Absent means the row cannot be
   * updated or deleted — mutating a synthesized id would hit a task that does
   * not exist.
   */
  persistedId?: string
  title: string
  /** "31 Jul", or "3 days late" once it is overdue. Machine value, so mono. */
  due?: string
  /** Whether `due` should read as late. */
  isLate: boolean
  /** Lowercase, as stored. */
  status: string
  rawStatus: string
  priority?: TaskPriority
  /** Closed rows show when they were finished instead of a status. */
  closedOn?: string
}

/**
 * Format a due date as the calendar day it actually is.
 *
 * These arrive as plain days ("2026-09-03"), which `Date` reads as UTC
 * midnight; formatting that in a local timezone west of UTC renders the day
 * before. Plain days are therefore split by hand, and only genuine timestamps
 * go through `Date`.
 */
function shortDate(iso?: string | null) {
  if (!iso) return undefined

  const plainDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  const date = plainDay
    ? new Date(Number(plainDay[1]), Number(plainDay[2]) - 1, Number(plainDay[3]))
    : new Date(iso)

  if (Number.isNaN(date.getTime())) return undefined
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" })
}

/**
 * Whole days a task is past due, compared date to date.
 *
 * Due dates arrive as plain days ("2026-07-29"), which `Date` reads as UTC
 * midnight. Comparing that against `Date.now()` marks a task due today as late
 * for anyone west of UTC, so both sides are flattened to a day first.
 */
function daysLate(iso?: string) {
  if (!iso) return 0

  const plainDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  const due = plainDay
    ? new Date(Number(plainDay[1]), Number(plainDay[2]) - 1, Number(plainDay[3]))
    : new Date(iso)
  if (Number.isNaN(due.getTime())) return 0

  const dueDay = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate())
  const now = new Date()
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())

  const diff = today - dueDay
  return diff > 0 ? Math.round(diff / 86400000) : 0
}

/**
 * A due date as a number to sort on. Undated sorts last rather than first,
 * which is what `new Date(undefined)` would otherwise give (NaN, comparing
 * false against everything and leaving the order untouched).
 */
function dueValue(iso?: string | null, missing = Infinity) {
  if (!iso) return missing
  const plainDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  const date = plainDay
    ? new Date(Number(plainDay[1]), Number(plainDay[2]) - 1, Number(plainDay[3]))
    : new Date(iso)
  const time = date.getTime()
  return Number.isNaN(time) ? missing : time
}

type Args = {
  candidateId: string
  jobId?: string | null
  /**
   * The job–candidate link, when the record was opened from a job. `GET /tasks`
   * filters on it directly, so "This job" is a server-side narrowing rather
   * than a guess at matching titles client-side.
   */
  jobCandidateId?: string | null
}

/**
 * The candidate record's tasks: the open list, the closed list, the counts the
 * header reads, and the four mutations.
 *
 * Same split as comments — this owns fetching, deriving and toasting, and the
 * view is handed rows to render. Both entry points use it, so a task behaves
 * the same from the candidates page and from a job's pipeline.
 */
export function useCandidateTasks({
  candidateId,
  jobId,
  jobCandidateId,
}: Args) {
  const canScopeToJob = Boolean(jobCandidateId)
  const [scope, setScope] = useState<TaskScope>(canScopeToJob ? "job" : "all")

  const params = useMemo(
    () =>
      ({
        entity_type: "candidate",
        entity_id: candidateId,
        ...(canScopeToJob && scope === "job"
          ? { job_candidate_id: jobCandidateId }
          : {}),
      }) as never,
    [candidateId, canScopeToJob, jobCandidateId, scope],
  )

  const {
    data,
    isFetching,
    isLoading,
    isError,
  } = useGetTasks(params, { query: { enabled: Boolean(candidateId) } })

  const queryClient = useQueryClient()
  const { mutateAsync: postTask, isPending: isPosting } = usePostTasks()
  const { mutateAsync: putTask, isPending: isSaving } = usePutTasksTaskId()
  const { mutateAsync: deleteTask, isPending: isDeleting } = useDeleteTasksTaskId()

  const refresh = useCallback(async () => {
    await invalidateTaskQueries(queryClient)
  }, [queryClient])

  // Scope is applied by the query itself, so nothing to narrow here.
  const all = useMemo(() => asArray<StoredTask>(data), [data])

  const toRow = useCallback((task: StoredTask, index: number): TaskRow => {
    const late = daysLate(task.due_date)
    const rawStatus = task.status ?? "todo"
    return {
      // Either id can be absent on a partially populated row; the index keeps
      // React keys unique rather than collapsing them onto `undefined`.
      id: task.task_id ?? task.id ?? `task-${index}`,
      persistedId: task.task_id ?? task.id,
      title: task.task_title ?? task.title ?? "Untitled task",
      due:
        late > 0
          ? `${late} day${late === 1 ? "" : "s"} late`
          : shortDate(task.due_date),
      isLate: late > 0,
      status: STATUS_TEXT[rawStatus] ?? rawStatus.toLowerCase(),
      rawStatus,
      priority: task.priority,
      closedOn: shortDate(task.completion_date),
    }
  }, [])

  /*
    Ordered by when it is due, soonest first, with undated tasks last.

    Nothing sorted these before, so the list was in whatever order the endpoint
    happened to return — and the record's rail, which shows four of them, was
    therefore showing an arbitrary four. On a candidate with thirty tasks that
    is worse than showing none, because it reads as "these are the ones that
    matter". A due date is the only ordering a task list has.
  */
  const byDue = useCallback((a: StoredTask, b: StoredTask) => {
    const at = dueValue(a.due_date)
    const bt = dueValue(b.due_date)
    if (at === bt) return 0
    return at - bt
  }, [])

  const open = useMemo(
    () =>
      all
        .filter((task) => task.status !== "done")
        .sort(byDue)
        .map(toRow),
    [all, byDue, toRow],
  )
  /** Closed tasks read newest-finished first: the last thing done is the news. */
  const closed = useMemo(
    () =>
      all
        .filter((task) => task.status === "done")
        .sort(
          (a, b) =>
            dueValue(b.completion_date, -Infinity) -
            dueValue(a.completion_date, -Infinity),
        )
        .map(toRow),
    [all, toRow],
  )
  const lateCount = useMemo(() => open.filter((task) => task.isLate).length, [open])

  const create = useCallback(
    async (
      title: string,
      dueDate: string,
      priority: TaskPriority,
      description = "",
    ) => {
      /*
        The endpoint takes one of two shapes, and they are exclusive: a
        job-scoped task is `job_candidate` with job_id + candidate_id, and an
        unscoped one is `candidate` with entity_id. Sending entity_id alongside
        job_id matches neither and is rejected.
      */
      const data = jobId
        ? {
            title,
            description,
            due_date: dueDate,
            entity_type: "job_candidate" as const,
            job_id: jobId,
            candidate_id: candidateId,
            priority,
            status: "todo",
          }
        : {
            title,
            description,
            due_date: dueDate,
            entity_type: "candidate" as const,
            entity_id: candidateId,
            priority,
            status: "todo",
          }

      try {
        await postTask({ data })
        await refresh()
        toast.success("Task added")
        return true
      } catch (error) {
        toast.error("Could not add the task", { description: `${error}` })
        return false
      }
    },
    [candidateId, jobId, postTask, refresh],
  )

  const setStatus = useCallback(
    async (taskId: string, status: string) => {
      try {
        await putTask({ taskId, data: { status } as never })
        await refresh()
        return true
      } catch (error) {
        toast.error("Could not update the task", { description: `${error}` })
        return false
      }
    },
    [putTask, refresh],
  )

  const destroy = useCallback(
    async (taskId: string) => {
      try {
        await deleteTask({ taskId })
        await refresh()
        toast.success("Task deleted")
        return true
      } catch (error) {
        toast.error("Could not delete the task", { description: `${error}` })
        return false
      }
    },
    [deleteTask, refresh],
  )

  return {
    open,
    closed,
    openCount: open.length,
    lateCount,
    isFetching,
    isLoading,
    isError,
    scope,
    setScope,
    canScopeToJob,
    create,
    setStatus,
    destroy,
    isBusy: isPosting || isSaving || isDeleting,
  }
}
